<script setup lang="ts">
import { toast } from '@/composables/baseToast'
import { supabase } from '@/composables/supabaseClient'
import numberToCurrency from '@/helpers/numberToCurrency'
import toastThrowError from '@/helpers/toastThrowError'
import { tryCatchAsync } from '@/helpers/tryCatch'
import uuidv7 from '@/helpers/uuidv7'
import { useDatabaseStore } from '@/stores/databaseStore'
import { useEstimateStore } from '@/stores/estimateStore'
import type {
  AssemblyVariables,
  AssemblyVariableValue,
  EstimateAssemblyVariableValues,
} from '@/types/customTypes'
import type { Tables } from '@/types/database.types'
import { computed, ref, toRaw, watch } from 'vue'
import AssemblyVariableInput from './AssemblyVariableInput.vue'
import BaseAutocomplete from './BaseAutocomplete.vue'
import type { DataTableColumn, DataTableGroup } from './BaseDataTable.vue'
import BaseDataTable from './BaseDataTable.vue'
import BaseModal from './BaseModal.vue'
import BasePopover from './BasePopover.vue'

const props = defineProps<{ estimateId: string }>()
const databaseStore = useDatabaseStore()
const estimateStore = useEstimateStore()
await estimateStore.loadEstimate(props.estimateId)

/**Returns the correctly formatted sort type name used as the row data key.
 * Formats as `__sortTypeId__<sortType.id>`
 */
function sortTypeDataKey(sortTypeId?: string) {
  return `__sortTypeId__${sortTypeId}`
}
const rows = computed(() => {
  const estimateSortTypes =
    estimateStore.estimateSortTypes?.filter((e) => e.estimate_id === props.estimateId) ?? []
  return (
    estimateStore.estimateItems
      ?.map((estimateItem) => {
        /**Maps estimate sort_type to sort_type_option_id */
        const estimateSortTypeMap: Record<string, string | undefined> = {}
        let isExcluded = false
        estimateSortTypes.forEach((est) => {
          const sortTypeOptionItem = estimateStore.estimateSortTypeOptionItems?.find(
            (sortTypeOptionItem) =>
              sortTypeOptionItem.estimate_item_id === estimateItem.id &&
              sortTypeOptionItem.estimate_sort_type_id === est.id,
          )
          const sortTypeOption = estimateStore.estimateSortTypeOptions?.find(
            (estimateSortTypeOption) =>
              estimateSortTypeOption.id === sortTypeOptionItem?.estimate_sort_type_option_id,
          )
          if (sortTypeOption && sortTypeOption.is_excluded) {
            isExcluded = true
          }
          estimateSortTypeMap[sortTypeDataKey(est.id)] =
            sortTypeOptionItem?.estimate_sort_type_option_id
        })
        return {
          ...estimateItem,
          ...estimateStore.computeEstimateItem(estimateItem),
          ...estimateSortTypeMap,
          is_excluded: isExcluded,
        }
      })
      .filter((row) => row.is_excluded === false) ?? []
  )
})

type Row = (typeof rows.value)[number]
const columns = computed<DataTableColumn<Row>[]>(() => {
  const computedColumns: DataTableColumn<Row>[] = [
    {
      name: 'Code',
      columnDataKey: 'code',
      columnType: 'disabled',
      defaultWidth: 125,
    },
    {
      name: 'Name',
      columnDataKey: 'name',
      columnType: 'ItemAssemblyNameColumn',
      defaultWidth: 300,
    },
    {
      name: 'Qty',
      columnDataKey: 'quantity',
      columnType: 'QuantityColumn',
      defaultWidth: 75,
    },
    {
      name: 'UOM',
      columnDataKey: 'uom_id',
      columnType: 'select',
      selectOptions: () =>
        databaseStore.unitsOfMeasure?.map((uom) => {
          return {
            name: uom.code,
            value: uom.id,
          }
        }),
      textDisplayFunction(value) {
        return databaseStore.unitsOfMeasure?.find((uom) => uom.id === value)?.code ?? ''
      },
      defaultWidth: 75,
    },
    {
      name: 'Material',
      columnDataKey: 'material_cost',
      columnType: 'number',
      textDisplayFunction: (value) => numberToCurrency(Number(value)),
    },
    {
      name: 'Labor',
      columnDataKey: 'labor_cost',
      columnType: 'number', // TODO - Create custom input element that changes the disabled state if the cell is computed from productivity rates
      textDisplayFunction: (value) => numberToCurrency(Number(value)),
    },
    {
      name: 'Sub',
      columnDataKey: 'subcontractor_cost',
      columnType: 'number',
      textDisplayFunction: (value) => numberToCurrency(Number(value)),
    },
    {
      name: 'Equipment',
      columnDataKey: 'equipment_cost',
      columnType: 'number',
      textDisplayFunction: (value) => numberToCurrency(Number(value)),
    },
    {
      name: 'Other',
      columnDataKey: 'other_cost',
      columnType: 'number',
      textDisplayFunction: (value) => numberToCurrency(Number(value)),
    },
    {
      name: 'Equipment Productivity',
      columnDataKey: 'equipment_productivity_per_hr',
      columnType: 'number',
      textDisplayFunction: (value) => (value ? `${value}/hr` : ''),
      deafultHidden: true,
    },
    {
      name: 'Labor Productivity',
      columnDataKey: 'labor_productivity_per_hr',
      columnType: 'number',
      textDisplayFunction: (value) => (value ? `${value}/hr` : ''),
      deafultHidden: true,
    },
    {
      name: 'Material Direct Cost',
      columnDataKey: 'material_direct_cost',
      columnType: 'disabled',
      textDisplayFunction: (value) => numberToCurrency(Number(value)),
      deafultHidden: true,
    },
    {
      name: 'Labor Direct Cost',
      columnDataKey: 'labor_direct_cost',
      columnType: 'disabled',
      textDisplayFunction: (value) => numberToCurrency(Number(value)),
      deafultHidden: true,
    },
    {
      name: 'Sub Direct Cost',
      columnDataKey: 'subcontractor_direct_cost',
      columnType: 'disabled',
      textDisplayFunction: (value) => numberToCurrency(Number(value)),
      deafultHidden: true,
    },
    {
      name: 'Equipment Direct Cost',
      columnDataKey: 'equipment_direct_cost',
      columnType: 'disabled',
      textDisplayFunction: (value) => numberToCurrency(Number(value)),
      deafultHidden: true,
    },
    {
      name: 'Other Direct Cost',
      columnDataKey: 'other_direct_cost',
      columnType: 'disabled',
      textDisplayFunction: (value) => numberToCurrency(Number(value)),
      deafultHidden: true,
    },
    {
      name: 'Material Price',
      columnDataKey: 'material_price',
      columnType: 'disabled',
      textDisplayFunction: (value) => numberToCurrency(Number(value)),
      deafultHidden: true,
    },
    {
      name: 'Labor Price',
      columnDataKey: 'labor_price',
      columnType: 'disabled',
      textDisplayFunction: (value) => numberToCurrency(Number(value)),
      deafultHidden: true,
    },
    {
      name: 'Sub Price',
      columnDataKey: 'subcontractor_price',
      columnType: 'disabled',
      textDisplayFunction: (value) => numberToCurrency(Number(value)),
      deafultHidden: true,
    },
    {
      name: 'Equipment Price',
      columnDataKey: 'equipment_price',
      columnType: 'disabled',
      textDisplayFunction: (value) => numberToCurrency(Number(value)),
      deafultHidden: true,
    },
    {
      name: 'Other Price',
      columnDataKey: 'other_price',
      columnType: 'disabled',
      textDisplayFunction: (value) => numberToCurrency(Number(value)),
      deafultHidden: true,
    },
    {
      name: 'Total Cost',
      columnDataKey: 'total_cost',
      columnType: 'disabled',
      textDisplayFunction: (value) => numberToCurrency(Number(value)),
      deafultHidden: true,
    },
    {
      name: 'Total Price',
      columnDataKey: 'total_price',
      columnType: 'disabled',
      textDisplayFunction: (value) => numberToCurrency(Number(value)),
      deafultHidden: true,
    },
    {
      name: 'Unit Cost',
      columnDataKey: 'unit_cost',
      columnType: 'disabled',
      textDisplayFunction: (value) => numberToCurrency(Number(value)),
      deafultHidden: true,
    },
    {
      name: 'Unit Price',
      columnDataKey: 'unit_price',
      columnType: 'disabled',
      textDisplayFunction: (value) => numberToCurrency(Number(value)),
      deafultHidden: true,
    },
    {
      name: 'Labor Hours',
      columnDataKey: 'labor_hours',
      columnType: 'disabled',
      textDisplayFunction: (value) => numberToCurrency(Number(value)),
      deafultHidden: true,
    },
    {
      name: 'Equipment Hours',
      columnDataKey: 'equipment_hours',
      columnType: 'disabled',
      textDisplayFunction: (value) => numberToCurrency(Number(value)),
      deafultHidden: true,
    },
    {
      name: 'Assembly',
      columnDataKey: 'estimate_assembly_id',
      columnType: 'disabled',
      textDisplayFunction: (value) => `${value}`,
      deafultHidden: true,
    },
  ]
  const sortTypeColumns: DataTableColumn<Row>[] =
    estimateStore.estimateSortTypes?.map((sortType) => {
      return {
        name: sortType.name,
        columnDataKey: sortTypeDataKey(sortType.id) as keyof Row,
        columnType: 'select',
        selectOptions: () =>
          estimateStore.estimateSortTypeOptions?.map((sortTypeOption) => {
            return {
              name: sortTypeOption.name,
              value: sortTypeOption.id,
            }
          }),
        textDisplayFunction: (value) =>
          estimateStore.estimateSortTypeOptions?.find((e) => e.id === value)?.name ?? '',
        defaultHidden: true,
      }
    }) ?? []
  return computedColumns.concat(sortTypeColumns)
})

const groupBySelection = ref<'Assembly' | 'Division' | 'Subdivision' | 'none'>('Assembly')

const groups: DataTableGroup<Row>[] = [
  {
    name: 'None',
  },
  {
    name: 'Assembly',
    fn: (row) => {
      const estimateAssembly = estimateStore.estimateAssemblies?.find(
        (a) => a.id === row.estimate_assembly_id,
      )
      const assembly = databaseStore.assemblies?.find((a) => a.id === estimateAssembly?.assembly_id)
      return estimateAssembly
        ? `${estimateAssembly.id}:${assembly?.code}:${estimateAssembly.name}`
        : '::No Assembly'
    },
  },
  {
    name: 'Subdivision',
    fn: (row) => {
      const item = databaseStore.items?.find((i) => i.id === row.item_id)
      const subDivision = databaseStore.itemDivisions?.find((d) => d.id === item?.item_division_id)
      const division = databaseStore.itemDivisions?.find((d) => d.id === subDivision?.id)
      return subDivision
        ? `${subDivision.id}:${division!.code}.${subDivision.code}:${subDivision.name}`
        : undefined
    },
  },
  {
    name: 'Division',
    fn: (row) => {
      const item = databaseStore.items?.find((i) => i.id === row.item_id)
      const subDivision = databaseStore.itemDivisions?.find((d) => d.id === item?.item_division_id)
      const division = databaseStore.itemDivisions?.find((d) => d.id === subDivision?.id)
      return division ? `${division.id}:${division.code}:${division.name}` : undefined
    },
  },
]

const dataTableKey = ref(1)

const newItemAssembly = ref<(typeof estimateStore.itemsAndAssemblies)[number]>()
const newAssemblyVariables = ref<AssemblyVariables>({})
const newAssemblyVariableValues = ref<Record<string, AssemblyVariableValue>>()
watch(newItemAssembly, () => {
  newAssemblyVariables.value = {}
})
const showNewItemAssemblyModal = ref(false)
let autocompleteKey = 0 // Used to replace the autocomplete with a new component to clear it

/**Configures modal to add a new assembly */
function addAssembly() {
  const assemblyId = newItemAssembly.value?.assembly_id
  if (!assemblyId) throw toastThrowError('assembly is not defined')
  const assembly = databaseStore.assemblies?.find((a) => a.id === assemblyId)
  newAssemblyVariables.value = assembly?.variables as AssemblyVariables
  showNewItemAssemblyModal.value = true
}

async function addEstimateItemOrAssembly(newRow: Row) {
  if (!newItemAssembly.value) throw toastThrowError('Must select an item or assembly to add')
  if (newItemAssembly.value.type === 'Assembly') {
    const assemblyId = newItemAssembly.value.assembly_id
    if (!assemblyId) throw toastThrowError('Unable to add assembly. Reload and retry.')
    const result = await tryCatchAsync(() =>
      estimateStore.createEstimateItemsByAssembly(
        assemblyId,
        toRaw(newAssemblyVariableValues.value ?? {}),
      ),
    )
    if (result.error)
      throw toastThrowError('Unable to add assembly. Reload and retry.', result.error)
  } else {
    const itemId = newItemAssembly.value.items[0].id
    if (!itemId) throw toastThrowError('Item not found. Reload and retry.')
    const result = await tryCatchAsync(() =>
      estimateStore.createEstimateItem(itemId, newRow.quantity),
    )
    if (result.error) throw toastThrowError('Unable to add item. Reload and retry.')
  }
  toast(`Added item(s) ${newItemAssembly.value?.name}`)
  showNewItemAssemblyModal.value = false
  newItemAssembly.value = undefined
  autocompleteKey += 1
}

async function updateEstimateItem(row: Row) {
  // TODO - update sort types
  const result = await supabase
    .from('estimate_item')
    .update({
      item_id: row.item_id,
      uom_id: row.uom_id,
      accounting_code: row.accounting_code,
      name: row.name,
      quantity: row.quantity,
      material_cost: row.material_cost,
      labor_cost: row.labor_cost,
      subcontractor_cost: row.subcontractor_cost,
      equipment_cost: row.equipment_cost,
      other_cost: row.other_cost,
      labor_productivity_per_hr: row.labor_productivity_per_hr,
      equipment_productivity_per_hr: row.equipment_productivity_per_hr,
    })
    .eq('id', row.estimate_item_id)
    .select()
    .single()
  if (result.error) throw toastThrowError('Unable to update item', result.error)
  for (const sortType of estimateStore.estimateSortTypes?.filter(
    (e) => e.estimate_id === props.estimateId,
  ) ?? []) {
    // Check and update the record for each sort type
    const keyName = sortTypeDataKey(sortType.id) as keyof Row
    if (row[keyName]) {
      const newSortTypeOptionId = row[keyName] as string // The sort type option id in the updated row data
      // NOT this may be undefined if no sort type has been selected
      // Check if a sort type exists with this sort type option and
      const sortTypeOptionItem = estimateStore.estimateSortTypeOptionItems?.find(
        (e) =>
          e.estimate_item_id === row.estimate_item_id && e.estimate_sort_type_id === sortType.id,
      )
      let resultData: Tables<'estimate_sort_type_option_item'>
      if (!sortTypeOptionItem) {
        const sortTypeOptionItemResult = await supabase
          .from('estimate_sort_type_option_item')
          .insert({
            id: uuidv7(),
            estimate_item_id: row.estimate_item_id,
            estimate_sort_type_id: sortType.id,
            estimate_sort_type_option_id: newSortTypeOptionId,
          })
          .select()
          .single()
        if (sortTypeOptionItemResult.error)
          throw toastThrowError('Unable to update sort type', sortTypeOptionItemResult.error)
        resultData = sortTypeOptionItemResult.data
      } else {
        const sortTypeOptionItemResult = await supabase
          .from('estimate_sort_type_option_item')
          .update({
            estimate_sort_type_option_id: newSortTypeOptionId,
          })
          .eq('id', sortTypeOptionItem.id)
          .select()
          .single()
        if (sortTypeOptionItemResult.error)
          throw toastThrowError('Unable to update sort type', sortTypeOptionItemResult.error)
        resultData = sortTypeOptionItemResult.data
      }
      const sortTypeOptionItemIndex = estimateStore.estimateSortTypeOptionItems?.findIndex(
        (e) => e.id === resultData.id,
      )
      if (sortTypeOptionItemIndex === undefined)
        throw toastThrowError('Unable to update sort type. Refresh page.')
      estimateStore.estimateSortTypeOptionItems?.splice(sortTypeOptionItemIndex, 1, resultData)
    }
  }
  const estimateItemIndex = estimateStore.estimateItems?.findIndex((e) => e.id === row.id)
  if (estimateItemIndex === undefined)
    throw toastThrowError('Unable to update estimate item locally. Refresh page.')
  estimateStore.estimateItems?.splice(estimateItemIndex, 1, result.data)
}

async function deleteEstimateItem(row: Row) {
  const result = await supabase.from('estimate_item').delete().eq('id', row.id)
  if (result.error) throw toastThrowError('Unable to delete item', result.error)
  const index = estimateStore.estimateItems?.findIndex((e) => e.id === row.id)
  if (index === undefined) throw toastThrowError('Unable to sync delete locally. Reload page.')
  estimateStore.estimateItems?.splice(index, 1)
  if (row.estimate_assembly_id) {
    const assemblyItemsRemaining = estimateStore.estimateItems?.filter(
      (ei) => ei.estimate_assembly_id === row.estimate_assembly_id,
    )
    if (assemblyItemsRemaining === undefined || assemblyItemsRemaining.length === 0) {
      // Estimate assembly no longer has items, delete it too
      const assemblyResult = await supabase
        .from('estimate_assembly')
        .delete()
        .eq('id', row.estimate_assembly_id)
      if (assemblyResult.error)
        throw toastThrowError('Unable to delete estimate assembly', assemblyResult.error)
      const assemblyIndex = estimateStore.estimateAssemblies?.findIndex(
        (ea) => ea.id === row.estimate_assembly_id,
      )
      if (assemblyIndex === undefined)
        throw toastThrowError('Unable to update local data. Refresh page.')
      estimateStore.estimateAssemblies?.splice(assemblyIndex, 1)
    }
  }
}

const editingEstimateAssembly = ref<Tables<'estimate_assembly'>>()

const showAssemblyEditModal = ref(false)

/**Configures assembly edit modal and opens it */
function editAssembly(estimateAssemblyId: string) {
  const estimateAssembly = estimateStore.estimateAssemblies?.find(
    (ea) => ea.id === estimateAssemblyId,
  )
  if (!estimateAssembly) throw toastThrowError('Unable to get estimate assembly')
  const assembly = databaseStore.assemblies?.find((a) => a.id === estimateAssembly.assembly_id)
  if (!assembly)
    throw toastThrowError(
      'Unable to locate assembly. Check that it still exists in the database and retry.',
    )
  editingEstimateAssembly.value = estimateAssembly
  showAssemblyEditModal.value = true
}

function closeAssemblyEdit() {
  editingEstimateAssembly.value = undefined
  showAssemblyEditModal.value = false
}

async function updateAssemblyItemsQty(
  estimateAssemblyId: string,
  variables: EstimateAssemblyVariableValues,
) {
  if (!editingEstimateAssembly.value)
    throw toastThrowError('Unable to get props from assembly modal')
  const estimateAssemblyItems =
    estimateStore.estimateItems?.filter((ei) => ei.estimate_assembly_id === estimateAssemblyId) ??
    []
  const updatedEstimateItems = structuredClone(toRaw(estimateStore.estimateItems)) ?? []
  await Promise.all(
    estimateAssemblyItems.map(async (estimateAssemblyItem) => {
      const updatedQty = estimateAssemblyItem.formula
        ? await estimateStore.calculateItemQtyFromAssemblyFormula(
            estimateAssemblyItem.formula,
            toRaw(variables),
          )
        : undefined
      const result = await supabase
        .from('estimate_item')
        .update({ quantity: updatedQty })
        .eq('id', estimateAssemblyItem.id)
        .select()
        .single()
      if (result.error)
        throw toastThrowError('Unable to update item. Refresh and retry', result.error)
      const index = updatedEstimateItems.findIndex((ei) => ei.id === estimateAssemblyItem.id)
      if (index === undefined) throw toastThrowError('Unable to update item locally. Reload page.')
      updatedEstimateItems.splice(index, 1, result.data)
    }),
  )
  const result = await supabase
    .from('estimate_assembly')
    .update({
      variable_values: toRaw(variables),
    })
    .eq('id', estimateAssemblyId)
    .select()
    .single()
  if (result.error) throw toastThrowError('Unable to update estimate assembly', result.error)
  const index = estimateStore.estimateAssemblies?.findIndex((ea) => ea.id === estimateAssemblyId)
  if (index === undefined) throw toastThrowError('Unable to update assembly locally. Reload page.')
  estimateStore.estimateAssemblies?.splice(index, 1, result.data)
  estimateStore.estimateItems = updatedEstimateItems
  closeAssemblyEdit()
}
</script>

<template>
  <div class="h-full w-full">
    <div class="overflow-auto">
      <BaseDataTable
        :key="dataTableKey"
        table-name="EstimateDetails"
        :rows="rows"
        :columns="columns"
        :row-key="'id'"
        show-new-row
        show-row-actions
        @row-update="updateEstimateItem"
        sortable-columns
        :groups="groups"
      >
        <template #groupHeaderCell="props">
          <button
            v-if="
              props.column.name === 'Qty' &&
              groupBySelection === 'Assembly' &&
              props.groupName.split(':')[2] !== 'No Assembly'
            "
            @click="editAssembly(props.groupName.split(':')[0])"
            @focus="editAssembly(props.groupName.split(':')[0])"
            class="flex h-full w-full cursor-pointer items-center justify-between"
          >
            <div class="h-full w-full border-b shadow"></div>
            <svg
              class="h-6 w-6 text-gray-800"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="m8 10 4 4 4-4"
              />
            </svg>
          </button>
          <button
            v-else-if="props.column.name === 'Name'"
            class="cursor-pointer font-bold"
            @click="props.toggleGroupCollapsed(props.groupName)"
          >
            {{ props.groupName.split(':')[2] }}
          </button>
          <button
            v-else-if="props.column.name === 'Code'"
            class="cursor-pointer font-bold"
            @click="props.toggleGroupCollapsed(props.groupName)"
          >
            {{ props.groupName.split(':')[1] }}
          </button>
        </template>
        <template #rowActions="props">
          <button
            @click="deleteEstimateItem(props.row)"
            class="cursor-pointer rounded hover:bg-gray-200"
          >
            <svg
              class="h-5 w-5 text-gray-800"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 7h14m-9 3v8m4-8v8M10 3h4a1 1 0 0 1 1 1v3H9V4a1 1 0 0 1 1-1ZM6 7h12v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V7Z"
              />
            </svg>
          </button>
        </template>
        <template #customCellEditor="props">
          <!-- TODO - Clear autocomplete when base  -->
          <BaseAutocomplete
            :key="autocompleteKey"
            v-if="props.column.columnType === 'ItemAssemblyNameColumn'"
            :suggestions="estimateStore.itemsAndAssemblies"
            suggestions-key="unique_id"
            :option-label="(v) => `${v.type} ${v.code} ${v.name}`"
            @select="(v) => (newItemAssembly = v)"
            placeholder="New Item or Assembly"
          />
          <input
            v-else-if="
              props.column.columnType === 'QuantityColumn' && newItemAssembly?.type === 'Item'
            "
            class="box h-full w-full border"
            type="number"
            min="0"
            v-model="props.newRow.quantity"
          />
          <BasePopover v-else-if="props.column.columnType === 'QuantityColumn'">
            <template #popoverTrigger>
              <button
                @click="addAssembly"
                @focus="addAssembly"
                class="flex h-full w-full cursor-pointer items-center justify-between"
              >
                <div class="h-full w-full border-b shadow"></div>
                <svg
                  class="h-6 w-6 text-gray-800"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="m8 10 4 4 4-4"
                  />
                </svg>
              </button>
            </template>
            <template #popoverBody>
              <div class="rounded border border-gray-300 p-3">
                <AssemblyVariableInput
                  :variables="newAssemblyVariables"
                  @save="
                    (variables) => {
                      newAssemblyVariableValues = variables
                      showNewItemAssemblyModal = false
                    }
                  "
                />
              </div>
            </template>
          </BasePopover>
          <span v-else></span>
        </template>
        <template #newRowActions="props">
          <button @click="addEstimateItemOrAssembly(props.newRow)" class="h-full w-full">
            <svg
              class="h-6 w-6 cursor-pointer text-gray-800"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                stroke="currentColor"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 5a1 1 0 0 1 1-1h11.586a1 1 0 0 1 .707.293l2.414 2.414a1 1 0 0 1 .293.707V19a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5Z"
              />
              <path
                stroke="currentColor"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 4h8v4H8V4Zm7 10a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
              />
            </svg>
          </button>
        </template>
      </BaseDataTable>
      <!-- <BaseModal
        v-model:show-modal="showNewItemAssemblyModal"
        @on-modal-close="
          () => {
            newAssemblyVariables = {}
            newAssemblyVariableValues = {}
            showNewItemAssemblyModal = false
          }
        "
      >
        <div class="flex flex-col gap-3">
          <AssemblyVariableInput
            :variables="newAssemblyVariables"
            @save="
              (variables) => {
                newAssemblyVariableValues = variables
                showNewItemAssemblyModal = false
              }
            "
          />
          <div class="flex gap-3">
            <button
              @click="
                () => {
                  newAssemblyVariables = {}
                  newAssemblyVariableValues = {}
                  showNewItemAssemblyModal = false
                }
              "
              type="button"
              class="w-full rounded-lg border border-gray-200 bg-white px-5 py-2.5 text-sm font-medium text-gray-900 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-4 focus:ring-gray-100 focus:outline-none"
            >
              Cancel
            </button>
          </div>
        </div>
      </BaseModal> -->
    </div>
    <BaseModal v-model:show-modal="showAssemblyEditModal" @on-modal-close="closeAssemblyEdit">
      <div class="flex flex-col gap-3">
        <AssemblyVariableInput
          v-if="editingEstimateAssembly"
          :variable-values="
            editingEstimateAssembly?.variable_values as Record<string, AssemblyVariableValue>
          "
          :variables="editingEstimateAssembly?.variables as AssemblyVariables"
          @save="(variables) => updateAssemblyItemsQty(editingEstimateAssembly!.id, variables)"
        />
        <div class="flex gap-3">
          <button
            @click="closeAssemblyEdit"
            type="button"
            class="w-full rounded-lg border border-gray-200 bg-white px-5 py-2.5 text-sm font-medium text-gray-900 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-4 focus:ring-gray-100 focus:outline-none"
          >
            Cancel
          </button>
        </div>
      </div>
    </BaseModal>
  </div>
</template>
